import { ShieldCheck } from "lucide-react";
import { infectionExtraCases } from "./infectionCases";

// Infection Control & Biosecurity — Cert IV: apply hygiene / infection control / WHS.
export const infectionConfig = {
  simId: "infection",
  simName: "VetSim Biosecurity",
  subtitle: "Infection control & biosecurity",
  title: "Infection Control & Biosecurity",
  accent: "#ca8a04",
  Icon: ShieldCheck,
  topic: "infection",
  steps: ["Risk-assess", "Don PPE", "Map the zones", "Isolation & barrier", "Clean / disinfect", "Waste & records"],
  introBlurb:
    "Protect patients, staff and the hospital from cross-infection. Risk-assess an infectious case, choose the right PPE, set up isolation and barrier nursing, apply the correct cleaning–disinfection–sterilisation level, then manage clinical waste and records. Graded against a Cert IV infection-control rubric.",
  cases: [
    ...infectionExtraCases,
    {
      id: "parvo-pup",
      name: "Pip — suspected parvovirus",
      meta: "12 wk pup · vomiting & bloody diarrhoea",
      briefing:
        "Pip presents collapsed with vomiting and haemorrhagic diarrhoea. The vet suspects canine parvovirus. The waiting room is full.",
      beats: [
        { type: "choice", key: "s1", label: "Risk-assess",
          title: "Risk-assess", prompt: "What's your first infection-control action?",
          options: [
            { t: "Treat as highly infectious immediately — get Pip out of the shared waiting room into isolation and limit who/what contacts her until parvo is ruled out.", score: 3, note: "Parvovirus is extremely contagious and hardy — isolate first, confirm later." },
            { t: "Leave her settled in the shared waiting room until the in-house test result comes back, then move her into isolation only if it's actually positive.", score: 0, note: "That risks contaminating the whole hospital and other patients." },
            { t: "Assume it's most likely a routine bout of gastro and handle her with the normal ward precautions until something proves otherwise.", score: 0, note: "Suspected parvo must be managed as infectious until excluded." },
          ], full: 20 },
        { type: "order", key: "s2", label: "Don PPE",
          title: "Don your PPE in the correct order", prompt: "Tap the PPE steps in the correct DONNING order for barrier nursing a parvo case.",
          items: [
            { id: "hh", text: "Perform hand hygiene" },
            { id: "gown", text: "Put on gown / apron" },
            { id: "face", text: "Put on eye / face protection" },
            { id: "gloves", text: "Put on gloves last (over the gown cuffs)" },
          ], full: 20 },
        { type: "floorplan", key: "s2b", label: "Map the zones",
          title: "Set up the hospital zones", prompt: "Lay out a safe floor plan for the infectious case. Tap a zone label, then tap the room it belongs in.",
          roles: [
            { id: "reception", label: "Reception / triage flow", color: "#0ea5e9" },
            { id: "clean", label: "Clean ward (well patients)", color: "#10b981" },
            { id: "ppe", label: "PPE & foot-bath station", color: "#f59e0b" },
            { id: "isolation", label: "Isolation (parvo pup)", color: "#ef4444" },
          ],
          zones: [
            { id: "front", label: "Front room (by entrance)", x: 4, y: 6, w: 44, h: 40, correct: "reception" },
            { id: "ward", label: "Main ward", x: 4, y: 52, w: 44, h: 42, correct: "clean" },
            { id: "corridor", label: "Rear corridor", x: 52, y: 6, w: 44, h: 40, correct: "ppe" },
            { id: "back", label: "Walled-off back room", x: 52, y: 52, w: 44, h: 42, correct: "isolation" },
          ],
          full: 20 },
        { type: "multi", key: "s3", label: "Isolation & barrier",
          title: "Isolation & barrier nursing", prompt: "Tap correct barrier-nursing practices.",
          chips: [
            { t: "Use dedicated equipment kept inside isolation", flag: true, coach: "Patient-dedicated kit prevents fomite spread to the rest of the hospital." },
            { t: "Assign a minimal, dedicated staff group to the case", flag: true, coach: "Fewer people in/out reduces transmission routes." },
            { t: "Clearly sign the isolation area and restrict entry", flag: true, coach: "Signage and access control protect everyone." },
            { t: "Nurse parvo patients last in the daily routine", flag: true, coach: "Working clean-to-dirty (infectious last) limits spread." },
            { t: "Carry the isolation thermometer back to the main ward", flag: false, coach: "" },
            { t: "Let students pop in to have a look", flag: false, coach: "" },
          ], max: 20 },
        { type: "choice", key: "s4", label: "Clean / disinfect",
          title: "Cleaning level", prompt: "Which disinfection approach suits a parvo environment?",
          options: [
            { t: "Clean off organic matter first, then use a disinfectant with proven parvocidal activity (e.g. accelerated hydrogen peroxide or appropriately diluted bleach) at the correct contact time.", score: 3, note: "Parvo is resistant — only parvocidal agents at the right dilution/contact time work, and only after removing organic soil." },
            { t: "A quick going-over with the general spray-and-wipe we use everywhere else should be perfectly adequate for the kennel and floor.", score: 0, note: "Routine disinfectants don't reliably kill parvovirus." },
            { t: "Just a thorough scrub with plenty of hot water should physically shift the virus, so reaching for a disinfectant isn't really necessary.", score: 0, note: "Water alone won't inactivate the virus." },
          ], full: 20 },
        { type: "choice", key: "s5", label: "Waste & records", last: true,
          title: "Waste & records", prompt: "How do you finish up?",
          options: [
            { t: "Dispose of contaminated waste in the correct clinical-waste stream, launder/dispose of PPE properly, record the case and cleaning done, and follow up on in-contact patients/vaccination advice.", score: 3, note: "Correct waste streams, PPE handling, documentation and contact follow-up." },
            { t: "Bag everything up and put it out with the normal general rubbish, since it's all heading to landfill anyway.", score: 0, note: "Infectious waste must go in the designated clinical-waste stream." },
            { t: "Skip the paperwork on this one, since it's really just a cleaning job rather than a clinical treatment worth documenting.", score: 0, note: "Infection-control actions and cleaning should be documented." },
          ], full: 20 },
      ],
    },
  ],
};
